import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  Spinner,
} from 'react-bootstrap';
import { ArrowBack, CloudUpload } from '@mui/icons-material';
import CommonHeader from 'layouts/dashboard/CommonHeader';
import { useToast } from 'context/toast-context/ToastProvider';
import UploadFile from 'components/courses/add-new-course/steps/UploadFile';
import courseIcon from '../../assets/images/Courses.svg';
import { vectorGenerator,getSignedUrl } from 'ServerAPI/serverAPI';
import axios from 'axios';


const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

const sanitizeFileName = (name) => {
  return name.trim().replace(/^\/+/, '');
};

const ResourceUpload = () => {
  const [cookies] = useCookies(['userId', 'organizationId']);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Auto-fill title from filename
  useEffect(() => {
    if (selectedFile) {
      setTitle(sanitizeFileName(selectedFile.name));
    } else {
      setTitle('');
    }
  }, [selectedFile]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setUploadError(null);
    setSelectedFile(null);

    if (!file) return;

    if (file.type !== 'application/pdf') {
      setUploadError('Only PDF files are allowed');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setUploadError('File size must be 25 MB or less');
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !cookies.organizationId) return;

    setIsUploading(true);
    setUploadError(null);

    const cleanFileName = sanitizeFileName(selectedFile.name);

    try {
      // Step 1: Get S3 presigned URL
      const payload = {
        resources: true,
        resourcefileName: cleanFileName,
        fileName: cleanFileName,
        method: 'PUT',
        userId: cookies.userId,
      };

      const { data } = await getSignedUrl(payload);
      const preSignedUrl = data?.preSignedUrl;
      if (!preSignedUrl) throw new Error('Failed to get upload URL');

      // Step 2: Upload to S3
      await axios.put(preSignedUrl, selectedFile, {
        headers: { 'Content-Type': 'application/pdf' },
      });

      // Step 3: Silently trigger vectorization (no await, no UI)
      vectorGenerator(selectedFile, cookies.organizationId).catch((err) => {
        // Fail silently — user already got success
        // console.error('Background vectorization failed:', err);
      });

      // Step 4: Show success & navigate
      showToast('Resource uploaded successfully!', 'success');
      setTimeout(() => navigate('/content-generation-dashboard'), 800);

    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Upload failed';
      setUploadError(msg);
      showToast(msg, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleBack = () => navigate('/content-generation-dashboard');

  return (
    <div className="container-fluid">
      <Row>
        <Col>
          <CommonHeader
            text="Upload Resource"
            subText="Upload a PDF (max 25 MB). Title will be set from file name."
            icon={courseIcon}
          />
        </Col>
      </Row>

      <Row className="mx-auto">
        <Col sm={10} md={8} className="mx-auto pt-5 px-0">
          <Card>
            <Card.Body className="p-4">

              {/* Title (Read-only) */}
              <div className="mb-4">
                <Form.Label className="fw-semibold">Resource Title</Form.Label>
                <Form.Control
                  type="text"
                  value={title}
                  readOnly
                  disabled
                  placeholder="Select a file to see title"
                  className="bg-light"
                />
                <Form.Text className="text-muted">
                  Auto-filled from file name (without .pdf)
                </Form.Text>
              </div>

              {/* Description
              <div className="mb-4">
                <Form.Label className="fw-semibold">Description (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Add notes about this resource..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={isUploading}
                  maxLength={500}
                />
                <Form.Text className="text-muted">
                  {description.length}/500
                </Form.Text>
              </div> */}

              {/* File Upload */}
              <div className="mb-4">
                <Form.Label className="fw-semibold">Upload PDF *</Form.Label>
                <UploadFile
                  fileType="pdf"
                  handleFileChange={handleFileChange}
                  showFileInfo
                  selectedFile={selectedFile}
                  disabled={isUploading}
                  accept=".pdf"
                />
                <Form.Text className="text-muted">
                  PDF only • Max 25 MB
                </Form.Text>
              </div>

              {/* Error */}
              {uploadError && <Alert variant="danger" className="mb-4">{uploadError}</Alert>}

              {/* Buttons */}
              <div className="d-flex gap-3">
                <Button
                  variant="secondary"
                  onClick={handleBack}
                  disabled={isUploading}
                >
                  <ArrowBack fontSize="small" className="me-1" />
                  Back
                </Button>

                <Button
                  variant="primary"
                  onClick={handleUpload}
                  disabled={isUploading || !selectedFile}
                  className="flex-grow-1"
                >
                  {isUploading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <CloudUpload fontSize="small" className="me-1" />
                      Upload Resource
                    </>
                  )}
                </Button>
              </div>

              {/* Simple upload progress */}
              {isUploading && (
                <Alert variant="info" className="mt-4">
                  <div className="d-flex align-items-center">
                    <Spinner size="sm" className="me-2" />
                    <div>
                      <strong>Uploading your file...</strong>
                      <br />
                      <small className="text-muted">Please wait</small>
                    </div>
                  </div>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ResourceUpload;